import React from 'react';
import { Link } from 'react-router-dom';
import { Radio, MapPin, User, Car, Trophy } from 'lucide-react';
import {
  TrackPositionBar, RigStatusBadge, isShiftLight, isCaution, isSessionInitialising, GridInitialisingPlaceholder,
} from '@/components/telemetry/TelemetryVisuals';
import RevStrip from '@/components/telemetry/RevStrip';
import FpsBadge from '@/components/telemetry/FpsBadge';
import { prettifyAssettoName, prettifyDriverName, isAcFamily } from '@/lib/telemetryPrettify';
import { useSpeedUnit, formatSpeed, speedUnitLabel } from '@/hooks/useSpeedUnit';

const THEMES = {
  porsche: {
    label: 'PORSCHE',
    sub: 'STUTTGART · GERMANY',
    accent: '#dd1f26',
    accentText: 'text-[#dd1f26]',
    gear: 'text-[#dd1f26]',
    glow: 'rgba(221,31,38,0.45)',
  },
  amg: {
    label: 'AMG',
    sub: 'AFFALTERBACH · GERMANY',
    accent: '#6ee7b7',
    accentText: 'text-emerald-300',
    gear: 'text-emerald-300',
    glow: 'rgba(110,231,183,0.4)',
  },
};

function fmtGear(g) {
  if (g === null || g === undefined) return '–';
  if (g < 0) return 'R';
  if (g === 0) return 'N';
  return String(g);
}

/**
 * Large themed live feed.
 *
 * With `onOpen` it acts on the screen it is on — the venue board opens the rig
 * detail in place — and without it, on the public page, it links through to the
 * rig's own page.
 */
export default function FeaturedRigCard({ rig, onOpen }) {
  const theme = THEMES[rig.theme] || THEMES.porsche;
  const last = rig.last_update ? new Date(rig.last_update).getTime() : 0;
  const live = last && (Date.now() - last < 6000);
  const standby = !last || (Date.now() - last >= 60000);
  const rpmPct = rig.max_rpm && rig.rpm ? Math.min(100, (rig.rpm / rig.max_rpm) * 100) : null;
  const shiftFlash = isShiftLight(rig);
  const showPos = live && rig.position != null && rig.position > 0;
  const speedUnit = useSpeedUnit();
  // Reporting, but the sim hasn't published its session metadata yet — hold a
  // placeholder rather than an empty cockpit.
  const initialising = isSessionInitialising(rig);

  // Display-side prettifier safety net for raw Kunos IDs / placeholder drivers.
  const displayDriver = prettifyDriverName(rig.driver_name, rig.rig_key) || 'Guest Driver';
  const displayCar = isAcFamily(rig.sim) ? prettifyAssettoName(rig.car) : rig.car;
  const displayTrack = isAcFamily(rig.sim) ? prettifyAssettoName(rig.track) : rig.track;

  const cardClass = `group relative block w-full text-left bg-[#0e0e12] border-t-2 border-x border-b border-white/10 overflow-hidden flex flex-col hover:border-primary/50 transition-colors ${shiftFlash ? 'animate-shift-flash' : ''}`;

  const body = (
    <>
      {/* header — dark card surface with brand icon glow */}
      <div className="bg-[#141414] px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 border-2 flex items-center justify-center"
            style={{ borderColor: theme.accent + '66', background: theme.accent + '15', boxShadow: `0 0 12px ${theme.glow}` }}
          >
            <Radio style={{ width: 18, height: 18, color: theme.accent }} />
          </div>
          <div>
            <p className="text-white font-black text-2xl tracking-[0.18em] leading-none">{theme.label}</p>
            <p className="text-white/50 text-[9px] font-bold tracking-[0.25em] mt-1">{theme.sub}</p>
          </div>
        </div>
        <RigStatusBadge rig={rig} size="lg" variant="pill" accent={theme.accent} />
      </div>

      {/* rig name strip */}
      <div className="bg-black/30 px-5 py-2 flex items-center justify-between">
        <span className="text-white font-bold text-sm tracking-wide uppercase truncate">{rig.rig_name || rig.rig_key}</span>
        <span className="text-white/40 text-[10px] font-mono tracking-widest uppercase">{rig.rig_key}</span>
      </div>

      {/* track position accent line (live only) */}
      {!standby && !initialising && (
        <div className="px-5 pt-3">
          <TrackPositionBar pct={rig.lap_dist_pct} accent={theme.accent} size="sm" />
        </div>
      )}

      {/* compact inline telemetry cluster — live/stale only; standby shows placeholder */}
      {standby ? (
        <div className="px-5 py-16 flex flex-col items-center justify-center gap-2 flex-1">
          <Radio className="w-6 h-6 text-white/15" />
          <p className="text-white/30 text-xs font-bold tracking-widest uppercase">Rig on Standby</p>
          <p className="text-white/20 text-[10px] font-medium">Awaiting Driver</p>
        </div>
      ) : initialising ? (
        /* COLD START — first packets in, session metadata not yet published */
        <GridInitialisingPlaceholder size="lg" className="flex-1" />
      ) : (
        <div className="p-5 flex-1 flex flex-col gap-4">
          {/* gear + speed inline cluster */}
          <div className="flex items-end gap-6">
            <div className="text-center">
              <p className="text-white/30 text-[9px] font-bold tracking-widest uppercase mb-1">Gear</p>
              <span className={`font-black text-5xl font-mono leading-none ${rig.gear && rig.gear > 0 ? theme.gear : 'text-white/50'}`}>
                {fmtGear(rig.gear)}
              </span>
            </div>
            <div className="flex-1">
              <p className="text-white/30 text-[9px] font-bold tracking-widest uppercase mb-1">Speed</p>
              <div className="flex items-baseline gap-2">
                <span className="text-white font-black text-4xl tabular-nums leading-none font-mono">
                  {formatSpeed(rig.speed_kph, speedUnit)}
                </span>
                <span className="text-white/40 text-[10px] font-bold tracking-widest px-1.5 py-0.5 border border-white/10 bg-black/30">{speedUnitLabel(speedUnit).toUpperCase()}</span>
              </div>
            </div>
          </div>

          {/* compact rev strip + fps */}
          <div className="flex items-center justify-between">
            <span className="text-white/30 text-[9px] font-bold tracking-widest uppercase">RPM</span>
            <FpsBadge fps={rig.fps} />
          </div>
          <RevStrip rpmPct={rpmPct} shiftFlash={shiftFlash} size="sm" />

          {/* driver/car + track chips */}
          <div className="flex flex-wrap items-center gap-2 mt-auto pt-3 border-t border-white/5">
            <span className="inline-flex items-center gap-1.5 border border-white/10 bg-black/30 px-2.5 py-1 text-white/70 text-xs font-medium">
              <User className="w-3 h-3 shrink-0" />
              <span className="truncate max-w-[110px]">{displayDriver}</span>
              <span className="text-white/20">/</span>
              <span className="truncate max-w-[100px]">{displayCar || '—'}</span>
            </span>
            <span className="inline-flex items-center gap-1.5 border border-white/10 bg-black/30 px-2.5 py-1 text-white/70 text-xs font-medium">
              <MapPin className="w-3 h-3 shrink-0" />
              <span className="truncate max-w-[160px]">{displayTrack || '—'}</span>
            </span>
            <span className="inline-flex items-center gap-1.5 border border-white/10 bg-black/30 px-2.5 py-1 text-xs font-medium">
              <Trophy className="w-3 h-3 shrink-0" style={{ color: theme.accent }} />
              <span className="text-white/40 text-[9px] font-bold tracking-widest uppercase">Best</span>
              <span className={`font-mono font-bold tabular-nums ${theme.accentText}`}>{rig.best_lap_time || '--:--.---'}</span>
            </span>
          </div>
        </div>
      )}
    </>
  );

  if (onOpen) {
    return (
      <button
        type="button"
        onClick={() => onOpen(rig.rig_key)}
        className={cardClass}
        style={{ borderTopColor: theme.accent }}
        aria-label={`Open ${rig.rig_name || rig.rig_key} live detail`}
      >
        {body}
      </button>
    );
  }

  return (
    <Link
      to={`/live/${rig.rig_key}`}
      className={cardClass}
      style={{ borderTopColor: theme.accent }}
    >
      {body}
    </Link>
  );
}